import type { RobotMediaItem } from '@/services/api/domains/robots.api'
import { formatRelativeTime } from '@/utils/formatRelativeTime'
import { Camera } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DashboardBridge } from '../Dashboard/DashboardBridge'
import { formatClockTime } from './alert-display'
import { formatLogDay, type PictureEntry } from './alert-log'
import { LogLocation } from './LogLocation'
import { PictureThumbButton } from './PictureThumbButton'

interface PictureLogRowProps {
  entry: PictureEntry
  /** The device's operator-facing label (wearer, else device); null when the roster has none. */
  label: string | null
  bridge: DashboardBridge | null
  onOpen: (media: RobotMediaItem) => void
}

/**
 * "Picture taken" as a log line. The time is when the picture REACHED the server: the device never
 * reports when the shutter fired (`helmet-media-record.ts` writes arrival time), and the row says
 * so in its tooltip rather than let a wall-clock time imply a precision it does not have.
 */
export function PictureLogRow({ entry, label, bridge, onOpen }: PictureLogRowProps) {
  const { t } = useTranslation('console')
  const { media, at } = entry
  const timeHint = t(
    'alerts.log.pictureTimeHint',
    'Time the picture reached the server. The device does not report when the shutter fired.',
  )

  return (
    <li className="flex items-start gap-x-3 p-3">
      <div className="w-14 shrink-0 text-right" title={timeHint}>
        <span className="font-mono text-[11px] text-gray-700 dark:text-gray-300">{formatClockTime(at)}</span>
        <span className="block text-[10px] text-gray-400 dark:text-gray-500">{formatLogDay(at)}</span>
      </div>

      <PictureThumbButton media={media} onOpen={onOpen} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Camera className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('alerts.log.pictureTaken', 'Picture taken')}
          </span>
          {label && <span className="truncate text-sm text-gray-600 dark:text-gray-300">{label}</span>}
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-400 dark:text-gray-500">
          <span title={timeHint}>{formatRelativeTime(at)}</span>
          <span aria-hidden>·</span>
          <LogLocation lng={media.lng} lat={media.lat} bridge={bridge} />
        </p>
      </div>
    </li>
  )
}
